import * as React from 'react'

import { Box, NoticeBox, Text } from '@factorialco/deprecated-design-system'
import { Features } from 'generated/featureDefinitions'

import { observer } from 'mobx-react'

import ErrorBoundary from 'modules/core/components/ErrorBoundary'
import Loading from 'modules/core/components/Loading'
import { Panes } from 'modules/core/components/Panes'
import { PanesPad } from 'modules/core/components/PanesPad'
import { useUnsafeCallback } from 'modules/core/hooks/useCallback'
import { useFeatureAvailability } from 'modules/core/hooks/useFeatureAvailability'
import i18n from 'modules/core/lib/i18n'

import BlankSlate from 'modules/reports/components/BlankSlate'
import Generator from 'modules/reports/components/Generator'
import { LoadingFieldsContext } from 'modules/reports/contexts/loadFieldsContext'
import { reportStoreContext } from 'modules/reports/contexts/reportStoreContext'
import { ValidationContext, useValidationContext } from 'modules/reports/contexts/validationContext'
import type { ReportEditorStore } from 'modules/reports/lib/Store'
import Report from 'modules/reports/store/models/Report'

import Carcasa from './Carcasa'
import Chart from './Chart'

const deprecatedReviewsPath = 'reviews_question_group.custom_field'
const deprecatedSurveysPath = 'surveys_question_group.custom_field'
const deprecatedEmployeeTeamPath = 'employee.team'
const deprecatedAtsTeamPath = 'ats_job_posting.team'

interface Props {
  store: ReportEditorStore
}

const ReportRenderer = observer(({ store }: Props) => {
  const { loading: loadingStore, reportStruct, variables } = store
  const [validation] = useValidationContext()

  const handleResult = useUnsafeCallback(
    (error: boolean) => {
      store.setBuilderResult(error)
    },
    [store]
  )

  if (loadingStore) return <Loading />

  if (!reportStruct.canRender) {
    let message = i18n.t('custom_reports.editor.missing')

    if (
      reportStruct.type === Report.CHART_TYPES.lines ||
      reportStruct.type === Report.CHART_TYPES.area ||
      reportStruct.type === Report.CHART_TYPES.bars
    ) {
      message = i18n.t('custom_reports.editor.missing_x_and_y')
    } else if (reportStruct.type === Report.CHART_TYPES.pie) {
      message = i18n.t('custom_reports.editor.missing_data_and_breakdown')
    }

    return <BlankSlate message={message} illustration="server-edit" />
  }

  if (!validation.isValid) {
    return (
      <BlankSlate
        message={i18n.t('custom_reports.editor.change_data_order')}
        description={i18n.t('custom_reports.editor.change_data_order_description')}
        illustration="Replace"
      />
    )
  }

  return (
    <PanesPad>
      <ErrorBoundary>
        <Generator reportStruct={reportStruct} variables={variables} onResult={handleResult}>
          {({ loading, data, error, hasTable }) => {
            return (
              <Chart
                data={data}
                error={error}
                reportStruct={reportStruct}
                loading={loading}
                hasTable={hasTable}
              />
            )
          }}
        </Generator>
      </ErrorBoundary>
    </PanesPad>
  )
})

interface EditorProps {
  store: ReportEditorStore
}

const Editor = ({ store }: EditorProps) => {
  const { loading } = store
  const { featureAvailability } = useFeatureAvailability(Features.analytics_advanced_reports)

  if (loading) return <Loading />

  if (featureAvailability.available) {
    const { projection } = store

    const hasDeprecatedReference = projection
      .convertToParams()
      .find(
        (params) =>
          params.path.includes(deprecatedReviewsPath) ||
          params.path.includes(deprecatedSurveysPath) ||
          params.path.includes(deprecatedEmployeeTeamPath) ||
          params.path.includes(deprecatedAtsTeamPath)
      )

    return (
      <reportStoreContext.Provider value={store}>
        <ValidationContext>
          <LoadingFieldsContext>
            <Box width="full">
              {hasDeprecatedReference && (
                <NoticeBox
                  color="grey000White"
                  iconColor="grey600"
                  border={{
                    bottom: {
                      style: 'solid',
                      width: 's1',
                      color: 'grey400',
                    },
                  }}
                  message={
                    <Text.Normal weight="medium">
                      {i18n.t('custom_reports.deprecated_sources_info')}
                    </Text.Normal>
                  }
                  alignItems="center"
                />
              )}
              <Panes direction="horizontal-2-cols-fixed-left" padding={false}>
                <Carcasa store={store} />
                <Box minHeight="full" justifyContent="center">
                  <ReportRenderer store={store} />
                </Box>
              </Panes>
            </Box>
          </LoadingFieldsContext>
        </ValidationContext>
      </reportStoreContext.Provider>
    )
  }

  return (
    <reportStoreContext.Provider value={store}>
      <Panes direction="vertical">
        <Box minHeight="full" justifyContent="center">
          <ReportRenderer store={store} />
        </Box>
      </Panes>
    </reportStoreContext.Provider>
  )
}

export default observer(Editor)
